// O que o cliente recebe primeiro quando um fluxo começa.
//
// Quem manda um fluxo — para uma conversa ou numa campanha — escolhe pelo
// nome, e nome engana: "Boas-vindas" e "Boas-vindas (antigo)" podem dizer
// coisas bem diferentes. Mostrar a primeira fala evita mandar o fluxo errado
// para um cliente de verdade.
//
// Anda a partir do gatilho pelo primeiro caminho, pulando o que não fala com
// o cliente (espera, etiqueta, fila, IA), até achar um passo que fala.

const ROTULO_DA_MIDIA = {
	image: "Imagem",
	video: "Vídeo",
	audio: "Áudio",
	document: "Documento",
	file: "Arquivo",
};

const rotuloDaMidia = (tipo, legenda) => {
	const rotulo = ROTULO_DA_MIDIA[tipo] || "Arquivo";

	return legenda ? `${rotulo} — “${legenda}”` : rotulo;
};

// {{nome}} e {{name}} viram o nome da pessoa, quando se sabe quem é. O resto
// das variáveis fica como está: só existe quando o fluxo roda.
const preencher = (texto, nome) => {
	const limpo = String(texto || "").trim();

	if (!nome) return limpo;

	return limpo.replace(/\{\{\s*(nome|name)\s*\}\}/gi, nome);
};

// Passos que falam com o cliente, e o que cada um diz.
const falaDoPasso = (no, nome) => {
	const dados = no.data || {};

	switch (no.type) {
		case "content": {
			const item = (dados.items || []).find(
				(i) => i && i.kind !== "delay" && String(i.value || "").trim(),
			);

			if (!item) return null;

			return item.kind === "text"
				? { texto: preencher(item.value, nome) }
				: { texto: rotuloDaMidia(item.kind, preencher(item.caption, nome)) };
		}

		case "media":
			return String(dados.url || "").trim()
				? {
						texto: rotuloDaMidia(
							dados.mediaType,
							preencher(dados.caption, nome),
						),
					}
				: null;

		case "menu":
		case "buttons":
		case "list":
			return {
				texto: preencher(dados.message, nome),
				opcoes: (dados.options || [])
					.map((opcao, indice) => ({
						numero: String(opcao?.number ?? indice + 1),
						rotulo: preencher(opcao?.label, nome),
					}))
					.filter((opcao) => opcao.rotulo),
				esperaResposta: true,
			};

		case "question":
			return {
				texto: preencher(dados.message, nome),
				esperaResposta: true,
			};

		case "end":
			return String(dados.message || "").trim()
				? { texto: preencher(dados.message, nome) }
				: null;

		default:
			return null;
	}
};

// Devolve um destes:
//  { tipo: "fala", texto, opcoes?, esperaResposta? }
//  { tipo: "outroNumero", numero } — o primeiro envio vai para um número
//    fixo, não para este cliente
//  { tipo: "ramifica" } — depende de uma condição ou sorteio
//  { tipo: "silencioso" } — o fluxo não fala nada (só etiqueta, fila…)
//  { tipo: "vazio" } — nada ligado ao gatilho: o servidor recusa
export const primeiraFalaDoFluxo = (fluxo, nome) => {
	const nos = Array.isArray(fluxo?.nodes) ? fluxo.nodes : [];
	const ligacoes = Array.isArray(fluxo?.edges) ? fluxo.edges : [];

	const gatilho = nos.find((no) => no?.type === "trigger");

	const seguinte = (id) => {
		const ligacao = ligacoes.find((l) => l?.source === id);

		return ligacao ? nos.find((no) => no?.id === ligacao.target) : null;
	};

	if (!gatilho) return { tipo: "vazio" };

	let atual = seguinte(gatilho.id);

	if (!atual) return { tipo: "vazio" };

	// Fluxo com volta ("pular para" um passo anterior) não pode prender a tela.
	const vistos = new Set();

	while (atual && !vistos.has(atual.id)) {
		vistos.add(atual.id);

		if (atual.type === "sendMessage") {
			return { tipo: "outroNumero", numero: atual.data?.number || "" };
		}

		if (atual.type === "condition" || atual.type === "randomizer") {
			return { tipo: "ramifica" };
		}

		const fala = falaDoPasso(atual, nome);

		if (fala) return { tipo: "fala", ...fala };

		if (atual.type === "end") return { tipo: "silencioso" };

		atual =
			atual.type === "jump"
				? nos.find((no) => no?.id === atual.data?.targetNodeId) ||
					seguinte(atual.id)
				: seguinte(atual.id);
	}

	return { tipo: "silencioso" };
};

// Uma linha curta do que o fluxo diz primeiro — é o que diferencia dois
// fluxos de nome parecido numa lista.
export const resumoDaPrimeiraFala = (fluxo) => {
	const previa = primeiraFalaDoFluxo(fluxo);

	if (previa.tipo === "fala") return previa.texto || "Menu sem texto";
	if (previa.tipo === "vazio") return "Nada ligado ao início ainda";
	if (previa.tipo === "ramifica") return "Começa por uma condição";
	if (previa.tipo === "outroNumero") return "Manda para um número fixo";

	return "Só ações, sem mensagem";
};

export default primeiraFalaDoFluxo;
